import type { VoiceDto } from '@shared/services/apiService/apiService';
import { isRecord } from '@shared/utils/isRecord';

import type {
  NumericVoiceSettingKey,
  VoiceFormDraft,
  VoiceFormField,
  VoiceFormFields,
  VoiceFormValues,
  VoiceProvider,
  VoiceSettingKey,
  VoiceSettingValue,
  VoiceSettingsObject,
} from './types';
import { providerSettingKeys, sonioxSampleRates } from './voiceSettings.presets';

export const VOICE_COLUMN_FIELDS = [
  'model',
  'language',
  'apiKey',
  'baseUrl',
] as const satisfies readonly VoiceFormField[];
const STRING_FIELDS = [
  'name',
  'previewUrl',
  'provider',
  'providerDisplayName',
  'voiceId',
  'gender',
  'accent',
] as const satisfies readonly VoiceFormField[];
export const VOICE_FORM_FIELDS = [...STRING_FIELDS, ...VOICE_COLUMN_FIELDS] as const;
const CREDENTIAL_FIELDS = ['apiKey', 'baseUrl'] as const;
/** Fields a typed JSON edit may promote; credentials must be moved by the user instead. */
export const JSON_PROMOTED_FIELDS = VOICE_FORM_FIELDS.filter(
  (key) => !CREDENTIAL_FIELDS.some((credential) => credential === key),
);

const isString = (value: unknown): value is string => typeof value === 'string';
const isStringOrNull = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

/** Whether a value fits its form field: any string, or null for the nullable column fields. */
export const isRepresentable = (key: VoiceFormField, value: unknown): value is string | null =>
  VOICE_COLUMN_FIELDS.some((field) => field === key) ? isStringOrNull(value) : isString(value);

/**
 * Worker spreads settings after the outer fields, so a representable JSON copy is the
 * effective value: it wins and moves into its field. Other JSON values stay untouched.
 */
export const resolveVoiceFormFields = (
  outer: Partial<Record<VoiceFormField, unknown>>,
  rawSettings: VoiceSettingsObject,
  promoted: readonly VoiceFormField[] = VOICE_FORM_FIELDS,
) => {
  const fields: VoiceFormFields = {};
  const settings = { ...rawSettings };
  const pick = <T>(key: VoiceFormField, accepts: (value: unknown) => value is T): T | undefined => {
    const copy = settings[key];
    if (promoted.includes(key) && Object.hasOwn(settings, key) && accepts(copy)) {
      delete settings[key];
      return copy;
    }
    const value = outer[key];
    return accepts(value) ? value : undefined;
  };
  for (const key of STRING_FIELDS) {
    const value = pick(key, isString);
    if (value !== undefined) fields[key] = value;
  }
  for (const key of VOICE_COLUMN_FIELDS) {
    const value = pick(key, isStringOrNull);
    if (value !== undefined) fields[key] = value;
  }
  return { fields, settings };
};

/** Edit hydration keeps each legacy row's effective runtime values, including NULL columns. */
export const hydrateVoiceForm = (voice: VoiceDto): VoiceFormDraft => {
  const saved: unknown = voice.voiceSettings;
  const { fields, settings } = resolveVoiceFormFields(voice, isRecord(saved) ? saved : {});
  return { ...fields, voiceSettings: settings };
};

type SettingsJsonResult =
  | { value: VoiceSettingsObject; error?: never }
  | { value?: never; error: 'invalidJson' | 'objectRequired' };

export const parseSettingsJson = (text: string): SettingsJsonResult => {
  try {
    const value: unknown = JSON.parse(text);
    if (!isRecord(value)) return { error: 'objectRequired' };
    return { value };
  } catch {
    return { error: 'invalidJson' };
  }
};

export const formatSettingsJson = (settings: VoiceSettingsObject) =>
  JSON.stringify(settings, null, 2);

const nestedKeys = ['voiceSettings', 'voice_settings'] as const;
const CREDENTIAL_KEYS = ['apiKey', 'baseUrl', 'api_key', 'base_url'] as const;

/** Removes keys from the nested containers that ElevenLabs flattens; other values stay. */
const dropNested = (settings: VoiceSettingsObject, keys: readonly string[]) => {
  for (const name of nestedKeys) {
    const nested = settings[name];
    if (!isRecord(nested)) continue;
    const siblings = { ...nested };
    for (const key of keys) delete siblings[key];
    settings[name] = siblings;
  }
};

/**
 * Credentials belong to the connection fields; JSON must not carry them at any spelling.
 * Known limitation: only root `apiKey`/`baseUrl` strings move to the connection fields on
 * hydration/Paste. Legacy aliases and nested copies stay visible here and must be removed manually.
 */
export const findCredentialKeys = (settings: VoiceSettingsObject): string[] => {
  const found: string[] = CREDENTIAL_KEYS.filter((key) => Object.hasOwn(settings, key));
  for (const name of nestedKeys) {
    const nested = settings[name];
    if (!isRecord(nested)) continue;
    for (const key of CREDENTIAL_KEYS) if (Object.hasOwn(nested, key)) found.push(`${name}.${key}`);
  }
  return found;
};

const aliases: Partial<Record<VoiceSettingKey, string>> = {
  similarityBoost: 'similarity_boost',
  useSpeakerBoost: 'use_speaker_boost',
  streamingLatency: 'streaming_latency',
  sampleRate: 'sample_rate',
  normalizeAudio: 'normalize_audio',
  instructionsPreset: 'instructions_preset',
  audioFormat: 'audio_format',
  taskType: 'task_type',
  refAudio: 'ref_audio',
  refText: 'ref_text',
};

export const readSetting = (
  settings: VoiceSettingsObject,
  provider: string,
  key: VoiceSettingKey,
): unknown => {
  // Match legacy flattening: root overrides nested, canonical spelling precedes alias.
  const nested =
    provider === '11labs'
      ? nestedKeys
          .map((name) => settings[name])
          .find(
            (value): value is VoiceSettingsObject =>
              isRecord(value) && Object.keys(value).length > 0,
          )
      : undefined;
  const effective = { ...nested, ...settings };
  if (Object.hasOwn(effective, key)) return effective[key];
  const alias = aliases[key];
  return alias && Object.hasOwn(effective, alias) ? effective[alias] : undefined;
};

export const writeSetting = <K extends VoiceSettingKey>(
  settings: VoiceSettingsObject,
  provider: string,
  key: K,
  value: VoiceSettingValue<NoInfer<K>> | undefined,
): VoiceSettingsObject => {
  const result = { ...settings };
  const alias = aliases[key];
  const spellings = alias ? [key, alias] : [key];
  for (const spelling of spellings) delete result[spelling];
  if (provider === '11labs') dropNested(result, spellings);
  if (value !== undefined) result[key] = value;
  return result;
};

const PROVIDER_FIELD_COPIES = ['voiceId', 'model', 'language', ...CREDENTIAL_KEYS] as const;

/**
 * A provider switch drops what belonged to the previous provider: every setting its controls
 * own (with aliases) and root copies of the reset fields, which Worker would still apply last.
 * Custom JSON keys that no control owns stay.
 */
export const resetProviderSettings = (
  settings: VoiceSettingsObject,
  previous: string,
): VoiceSettingsObject => {
  let result = { ...settings };
  for (const key of PROVIDER_FIELD_COPIES) delete result[key];
  // ElevenLabs also reads nested settings, so drop nested copies of the reset fields there.
  if (previous === '11labs') dropNested(result, PROVIDER_FIELD_COPIES);
  for (const key of providerSettingKeys(previous)) {
    result = writeSetting(result, previous, key, undefined);
  }
  return result;
};

/**
 * Choosing a provider in the select. The first choice keeps earlier input because nothing
 * provider-specific exists yet; a later switch clears every provider-bound field and setting.
 */
export const switchProvider = (form: VoiceFormValues, provider: string): VoiceFormValues => {
  const reset = Boolean(form.provider);
  let settings = reset
    ? resetProviderSettings(form.voiceSettings, form.provider)
    : form.voiceSettings;
  // The select is an explicit edit, so it replaces a JSON provider copy.
  if (isString(settings.provider)) {
    settings = { ...settings };
    delete settings.provider;
  }
  const cleared = reset
    ? { voiceId: '', model: null, language: null, apiKey: null, baseUrl: null }
    : {};
  return { ...form, ...cleared, provider, voiceSettings: settings };
};

/** A format change drops a sample rate the new Soniox format cannot carry. */
export const writeControlSetting = <K extends VoiceSettingKey>(
  settings: VoiceSettingsObject,
  provider: string,
  key: K,
  value: VoiceSettingValue<NoInfer<K>> | undefined,
): VoiceSettingsObject => {
  const result = writeSetting(settings, provider, key, value);
  if (provider !== 'soniox' || key !== 'audioFormat') return result;
  const rate = readSetting(result, provider, 'sampleRate');
  return rate === undefined || sonioxSampleRates(value).some((allowed) => allowed === rate)
    ? result
    : writeSetting(result, provider, 'sampleRate', undefined);
};

export type NumberRule = { min?: number; max?: number; integer?: true };

// Bounds mirror the API settings DTOs.
const NUMBER_RULES = {
  stability: { min: 0, max: 1 },
  similarityBoost: { min: 0, max: 1 },
  style: { min: 0, max: 1 },
  streamingLatency: { min: 0, max: 4, integer: true },
  speed: { min: 0, max: 2 },
  expressiveness: { min: 0, max: 1 },
  bitrate: { min: 1, integer: true },
} satisfies Partial<Record<NumericVoiceSettingKey, NumberRule>>;

export type NumericSettingKey = keyof typeof NUMBER_RULES;

/** The same key can carry different provider bounds, e.g. Hamsa speed 0–2 and OpenAI 0.25–4. */
const NUMBER_RULE_OVERRIDES: Partial<
  Record<VoiceProvider, Partial<Record<NumericSettingKey, NumberRule>>>
> = {
  openai: { speed: { min: 0.25, max: 4 } },
};

export const numberRule = (provider: string, key: NumericSettingKey): NumberRule =>
  NUMBER_RULE_OVERRIDES[provider as VoiceProvider]?.[key] ?? NUMBER_RULES[key];

export const validNumberInput = (
  provider: string,
  key: NumericSettingKey,
  text: string,
): boolean => {
  const rule = numberRule(provider, key);
  const value = Number(text);
  return (
    text.trim() !== '' &&
    Number.isFinite(value) &&
    (!rule.integer || Number.isInteger(value)) &&
    (rule.min === undefined || value >= rule.min) &&
    (rule.max === undefined || value <= rule.max)
  );
};
